/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useRef, useCallback, useEffect } from 'react';
import { Branch, QUESTIONNAIRE_ELEMENT_TYPE } from '../../questionnaireTemplate.enums';
import { QuestionnaireDragSource } from '../../questionnaireTemplate.types';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import { formElementSchema } from '../schemas/formElement.schema';
import { createNewSchema } from '../utils/createNewSchema';
import { handleElementBlock } from '../utils/handleElementBlock';

interface DragPosition {
  x: number;
  y: number;
}

/**
 * `useDragAndDrop` is a custom React hook that provides drag-and-drop functionality
 * for building questionnaire templates. It manages the state of dragged elements,
 * detects hovered drop zones, and handles the logic for adding and moving elements
 * within the questionnaire form builder.
 *
 * It interacts with the `QuestionnaireTemplateStore` to update the UI state
 * (e.g., currently dragged element, hovered zone) and the actual questionnaire
 * structure (e.g., adding new elements, reordering existing ones).
 *
 * @returns An object containing handler functions for drag-and-drop events:
 *   - `handleDragStart`: Initiates a drag operation.
 *   - `handleDragMove`: Updates the position of the drag preview and detects potential drop zones.
 *   - `handleDragEnd`: Finalizes the drag operation, either adding a new element or moving an existing one.
 */
export const useDragAndDrop = () => {
  // Request Animation Frame ID to optimize drag movement updates.
  const rafId = useRef<number | null>(null);
  // Reference to the ID of the last hovered drop zone to prevent redundant state updates.
  const lastHoveredZoneRef = useRef<string | null>(null);
  // Reference to the DOM element used as the drag preview (the "ghost" image that follows the cursor).
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);
  // Stores the offset between the cursor position and the top-left corner of the dragged element
  // at the start of the drag, ensuring the drag preview aligns correctly with the cursor.
  const dragOffsetRef = useRef<DragPosition>({ x: 0, y: 0 });
  const globalCleanupRef = useRef<(() => void) | null>(null);
  // Access the entire store state.
  const store = QuestionnaireTemplateStore.useStore();

  // Actions to modify the UI and data state in the QuestionnaireTemplateStore.
  const setDraggedElement = QuestionnaireTemplateStore.useStoreActions((actions) => actions.setDraggedElement);
  const setHoveredDropZone = QuestionnaireTemplateStore.useStoreActions((actions) => actions.setHoveredDropZone);
  const moveElement = QuestionnaireTemplateStore.useStoreActions((actions) => actions.moveElement);
  const addElement = QuestionnaireTemplateStore.useStoreActions((actions) => actions.addElement);
  const clearUI = QuestionnaireTemplateStore.useStoreActions((actions) => actions.clearUI);

  useEffect(() => {
    return () => {
      if (globalCleanupRef.current) {
        window.removeEventListener('pointerup', globalCleanupRef.current);
        window.removeEventListener('pointercancel', globalCleanupRef.current);
      }
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, []);
  /**
   * Handles the start of a drag operation.
   * - Captures the pointer to ensure subsequent drag events are sent to this handler.
   * - Determines if the dragged item is a new element from the toolbox or an existing element from the canvas.
   * - Calculates the offset for the drag preview.
   * - Updates the store with information about the dragged element.
   *
   * @param e The React PointerEvent.
   * @param sourceData Data about the element being dragged (id, type, whether it's new).
   */
  const handleDragStart = useCallback(
    (e: React.PointerEvent, sourceData: { id?: string; type: QUESTIONNAIRE_ELEMENT_TYPE; isNew: boolean }) => {
      const target = e.currentTarget as HTMLElement;
      // Capture the pointer to ensure drag events are consistent even if the pointer
      // leaves the initial element.
      target.setPointerCapture(e.pointerId);

      // Extract parentId and branch from data attributes, if available, for existing elements.
      const parentId = target.getAttribute('data-parent-id') || null;
      const branch = (target.getAttribute('data-branch') as Branch) || null;

      // Calculate the current index of an existing element within its parent's ordering.
      let currentIndex = -1;
      if (!sourceData.isNew && sourceData.id) {
        const key = parentId && branch ? `${parentId}-${branch}` : 'root';
        currentIndex = store.getState().ordering[key]?.indexOf(sourceData.id) ?? 0;
      }

      // Define the source of the drag (toolbox for new, canvas for existing).
      const source: QuestionnaireDragSource = {
        type: sourceData.isNew ? 'toolbox' : 'canvas',
        templateType: sourceData.type,
        parentId,
        branch,
        index: currentIndex,
      };

      // Calculate the offset (where the user clicked relative to the element's top-left)
      // This ensures the drag preview appears directly under the cursor.
      const rect = target.getBoundingClientRect();
      dragOffsetRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      // Set the dragged element in the global store.
      setDraggedElement({ elementId: sourceData.id || 'new-item', source });
      // fires if the element is unmounted mid-drag or pointer capture is silently lost
      const cleanup = () => {
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
          rafId.current = null;
        }
        clearUI();
        globalCleanupRef.current = null;
        window.removeEventListener('pointerup', cleanup);
        window.removeEventListener('pointercancel', cleanup);
      };

      globalCleanupRef.current = cleanup;
      window.addEventListener('pointerup', cleanup);
      window.addEventListener('pointercancel', cleanup);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDraggedElement],
  );

  /**
   * Handles the movement of the dragged element.
   * - Updates the visual position of the drag preview ("ghost").
   * - Uses `requestAnimationFrame` to debounce updates for performance.
   * - Detects the closest valid drop zone based on the cursor position.
   * - Performs a blocking check for nested Yes/No elements (prevents nesting logic elements).
   * - Updates the store with information about the currently hovered drop zone.
   *
   * @param e The React PointerEvent.
   */
  const handleDragMove = useCallback(
    (e: React.PointerEvent) => {
      const { dragSource } = store.getState().ui;
      // If the store has been cleared (e.g., drag ended unexpectedly), stop immediately.
      if (!dragSource) {
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
          rafId.current = null;
        }
        return;
      }

      // Update the position of the drag preview ("ghost") DOM element.
      // The offset ensures the cursor stays at the same relative position within the preview.
      /* istanbul ignore next */
      if (dragPreviewRef.current) {
        dragPreviewRef.current.style.transform = `translate3d(${e.clientX - dragOffsetRef.current.x}px, ${
          e.clientY - dragOffsetRef.current.y
        }px, 0)`;
      }

      // Use requestAnimationFrame to throttle expensive DOM queries and state updates
      // for smoother drag animations.
      if (rafId.current) return;

      rafId.current = requestAnimationFrame(() => {
        // Query all elements marked as drop zones.
        const allZones = Array.from(document.querySelectorAll<HTMLElement>('[data-dropzone]'));

        let closestZoneRaw = null;
        let minDistance = Infinity;
        let closestZoneData: any = null; // Store parsed data for the closest zone.
        let closestZone: any = null;

        // Iterate through all potential drop zones to find the one closest to the cursor.
        allZones.forEach((zone) => {
          const rect = zone.getBoundingClientRect();

          // Define a buffer around the zone for easier targeting.
          const bufferPadding = 20;
          // Check if the cursor is horizontally within the zone's extended bounds.
          const isHorizontallyInside = e.clientX >= rect.left - bufferPadding && e.clientX <= rect.right + bufferPadding;
          if (!isHorizontallyInside) return;

          // Calculate vertical distance from cursor to the horizontal center-line of the zone.
          // This helps in determining the "closest" zone when multiple zones are vertically aligned.
          const zoneCenterY = rect.top + rect.height / 2;
          const distance = Math.abs(e.clientY - zoneCenterY);
          // If this zone is closer than the previously found closest zone, update.
          if (distance < minDistance) {
            minDistance = distance;
            closestZoneRaw = zone.getAttribute('data-dropzone');
            closestZoneData = JSON.parse(closestZoneRaw || /* istanbul ignore next */ '{}');
            closestZone = zone;
          }
        });

        // Update the store ONLY if the "winning" hovered zone has changed,
        // reducing unnecessary re-renders.
        if (lastHoveredZoneRef.current !== closestZoneRaw) {
          lastHoveredZoneRef.current = closestZoneRaw;

          if (closestZoneRaw) {
            const zoneData = JSON.parse(closestZoneRaw);
            const { isBlocked, isMaxDepthLimit } = handleElementBlock({ closestZone, closestZoneData, dragSource });

            setHoveredDropZone({ ...zoneData, isBlocked, isMaxDepthLimit });
          } else {
            // If no zone is hovered, clear the hovered drop zone in the store.
            setHoveredDropZone(null);
          }
        }

        // Clear the rafId so a new animation frame can be requested for the next movement.
        rafId.current = null;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setHoveredDropZone],
  );

  /**
   * Handles the end of a drag operation (when the pointer is released).
   * - Releases the pointer capture.
   * - Checks if the drop was blocked by validation rules (e.g., nested Yes/No).
   * - If a valid drop zone is hovered, commits the change:
   *   - If it's a new element from the toolbox, adds it to the questionnaire structure.
   *   - If it's an existing element, moves it to the new position.
   * - Cleans up UI state related to dragging.
   *
   * @param e The React PointerEvent.
   */
  const handleDragEnd = useCallback(
    (e: React.PointerEvent) => {
      // Release the pointer capture from the target element.
      const target = e.currentTarget as HTMLElement;
      if (target.hasPointerCapture(e.pointerId)) {
        target.releasePointerCapture(e.pointerId);
      }

      if (globalCleanupRef.current) {
        window.removeEventListener('pointerup', globalCleanupRef.current);
        window.removeEventListener('pointercancel', globalCleanupRef.current);
        globalCleanupRef.current = null;
      }

      const { draggedElementId, dragSource, hoveredDropZone } = store.getState().ui;

      // If the drop is blocked by a validation rule, clear UI and exit.
      if (hoveredDropZone?.isBlocked) {
        clearUI();
        return;
      }

      // If there's a dragged element and a valid hovered drop zone, commit the change.
      if (dragSource && hoveredDropZone) {
        if (dragSource.type === 'toolbox') {
          const type = formElementSchema[dragSource.templateType].questionType;
          const newElement = createNewSchema(type, hoveredDropZone.parentId, hoveredDropZone.branch);

          addElement({
            element: newElement,
            parentId: hoveredDropZone.parentId,
            branch: hoveredDropZone.branch,
            index: hoveredDropZone.index,
          });
        } else if (draggedElementId) {
          // Scenario 2: Moving an EXISTING element on the canvas.
          moveElement({
            elementId: draggedElementId,
            newParentId: hoveredDropZone.parentId,
            newBranch: hoveredDropZone.branch,
            newIndex: hoveredDropZone.index,
          });
        }
      }

      // Clean up any lingering animation frame requests.
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }

      // Clear all drag-related UI state from the store.
      clearUI();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moveElement, clearUI],
  );

  return {
    handleDragStart,
    handleDragMove,
    handleDragEnd,
  };
};
